/**
 * Iter53 P40 — Universal CSV export button (Sales / Clients / Contracts).
 *
 * Fetches a lightweight CSV from `/api/exports/{entity}.csv` and triggers a
 * browser download with a timestamped filename. Handles auth token via axios
 * default headers (already configured globally in AuthContext).
 *
 * Iter68m — pentru răspunsuri mari/lente (ex. export control SF ~3,7 MB, ~50s)
 * descărcarea automată programatică (a.click() după un await lung) nu e onorată
 * fiabil de browser: UI ieșea din „Se generează…" dar fișierul nu ajungea în
 * Descărcări. Fix: după ce fișierul e pregătit păstrăm Blob URL-ul și afișăm un
 * link vizibil „Descarcă fișierul" (click = gest utilizator → descărcare sigură,
 * reîncercabilă). Auto-descărcarea rămâne best-effort. URL-ul e eliberat la
 * înlocuirea fișierului și la demontarea componentei.
 *
 * Props:
 *   - entity:   "sales" | "sales_sf_control" | "clients" | "contracts"   (required)
 *   - filters:  {date_from, date_to, agency_id, agent_ref_id, supplier, commodity, client_type, county}
 *               Only the params relevant to the entity are forwarded.
 *   - label:    Custom button label (default: "Export CSV")
 *   - variant:  Button variant passthrough (default: "outline")
 *   - size:     Button size passthrough (default: "sm")
 *   - testId:   data-testid for the button (default: `export-csv-${entity}-btn`)
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Download, Loader2, CheckCircle2, X } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Which query params make sense for each entity (all others are silently ignored)
const ALLOWED_FILTERS = {
  sales:     ["date_from", "date_to", "agency_id", "agent_ref_id", "sale_ids", "limit"],
  sales_sf_control: ["date_from", "date_to", "agency_id", "agent_ref_id", "sale_ids", "limit"],
  clients:   ["client_type", "county", "limit"],
  contracts: ["date_from", "date_to", "supplier", "commodity", "limit"],
};

export const ExportCsvButton = ({
  entity,
  filters = {},
  queryParams = null,
  label = "Export CSV",
  variant = "outline",
  size = "sm",
  testId,
}) => {
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(null); // { url, filename, sizeKb } | null
  const urlRef = useRef(null);
  const btnTestId = testId || `export-csv-${entity}-btn`;

  // Eliberează Blob URL-ul la demontarea componentei (evită memory leak).
  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  const setReadyFile = (url, filename, sizeKb) => {
    // Înlocuim fișierul anterior → eliberăm URL-ul vechi înainte de a-l pierde.
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setReady({ url, filename, sizeKb });
  };

  const clearReady = () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setReady(null);
  };

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      let params;
      if (queryParams) {
        // Mod „aceleași filtre ca lista": URLSearchParams complet, trimis ca atare
        params = new URLSearchParams(queryParams);
        params.delete("page");
        params.delete("limit");
      } else {
        const allowed = ALLOWED_FILTERS[entity] || [];
        params = {};
        for (const k of allowed) {
          const v = filters[k];
          if (v !== null && v !== undefined && v !== "") params[k] = v;
        }
      }

      const response = await axios.get(`${API_URL}/exports/${entity}.csv`, {
        params,
        responseType: "blob",
      });

      // Extract filename from Content-Disposition (backend sets it), fallback to timestamp
      const cd = response.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const now = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 15);
      const filename = (match && match[1]) || `${entity}_${now}.csv`;

      // Validăm că răspunsul e un CSV real, nu o eroare HTML/JSON salvată cu .csv.
      const ct = (response.headers["content-type"] || "").toLowerCase();
      const raw = response.data;
      const blob = raw instanceof Blob
        ? raw
        : new Blob([raw], { type: "text/csv;charset=utf-8" });
      if (!blob || blob.size === 0) {
        toast.error("Export gol — răspunsul serverului nu conține date");
        return;
      }
      if (ct && !ct.includes("csv") && !ct.includes("octet-stream")) {
        // Răspuns neașteptat (ex. HTML/JSON de eroare) — nu-l oferim ca CSV.
        toast.error("Răspuns invalid de la server — exportul nu este un fișier CSV");
        return;
      }

      const url = URL.createObjectURL(blob);
      setReadyFile(url, filename, Math.round(blob.size / 1024));

      // Best-effort: încearcă descărcarea automată (merge pentru fișiere mici/rapide).
      try {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (_) {
        /* dacă browserul blochează auto-descărcarea, linkul vizibil rămâne disponibil */
      }

      // Mesaj onest: fișierul e pregătit pe client; nu afirmăm că browserul l-a salvat.
      toast.success("Fișier pregătit — dacă descărcarea nu pornește, apasă «Descarcă fișierul»");
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 403
        ? "Nu ai permisiuni pentru acest export"
        : status === 401
        ? "Sesiune expirată — te rog să te autentifici din nou"
        : err?.code === "ECONNABORTED"
        ? "Export întrerupt (timeout) — încearcă un interval de filtrare mai mic"
        : "Eroare la generare export CSV";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <Button
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={loading}
        data-testid={btnTestId}
        title={`Descarcă ${entity} ca fișier CSV`}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Download className="h-4 w-4 mr-2" />
        )}
        {loading ? "Se generează…" : label}
      </Button>

      {ready && (
        <span
          className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-800"
          data-testid={`${btnTestId}-ready`}
        >
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          <a
            href={ready.url}
            download={ready.filename}
            className="font-medium underline underline-offset-2 hover:text-emerald-900"
            data-testid={`${btnTestId}-download-link`}
            title={`${ready.filename} (${ready.sizeKb} KB)`}
          >
            Descarcă fișierul
          </a>
          <span className="text-emerald-600">({ready.sizeKb} KB)</span>
          <button
            type="button"
            onClick={clearReady}
            className="ml-1 rounded p-0.5 hover:bg-emerald-100"
            data-testid={`${btnTestId}-dismiss`}
            title="Închide"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      )}
    </span>
  );
};

export default ExportCsvButton;
